import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SkeletonDashboard } from "@/components/skeleton-dashboard";
import {
      Breadcrumb,
      BreadcrumbItem,
      BreadcrumbList,
      BreadcrumbPage,
      BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { UpgradeBanner } from "@/components/upgrade-banner";
import { ProductDisplay } from "@/components/upgrade-plan/product-display";
import { useSession } from "@/hooks/use-session";
import {
      getDashboardBreadcrumbDisplayText,
      getSearchSpecificationIdFromPath,
} from "@/lib/dashboard-breadcrumbs";
import {
      shouldDeferDashboardBrandResolution,
      shouldRedirectDashboardToBrandOnboarding,
} from "@/lib/dashboard-routing";
import { useTRPC } from "@/lib/trpc";
import { toTitleCase } from "@/lib/utils";
import { useBrandStore } from "@/state/store/brand";
import { usePricingPageStore } from "@/state/store/pricingPageStore";
import { useSearchSpecificationStore } from "@/state/store/searchSpecification";

export const Route = createFileRoute("/_dashboard")({
      component: RouteComponent,
});

const UNBRANDED_ROUTES = new Set([
      "/team",
      "/usage",
      "/billing",
      "/notifications",
      "/settings",
      "/pricing",
      "/activity",
      "/api-access",
      "/superadmin",
]);

function RouteComponent() {
      const navigate = useNavigate();
      const { sessionData, isLoading: isLoadingSession } = useSession();

      useEffect(() => {
            if (!isLoadingSession && !sessionData) {
                  navigate({ to: "/auth", replace: true });
            }
      }, [isLoadingSession, navigate, sessionData]);

      if (isLoadingSession || !sessionData) {
            return <SkeletonDashboard />;
      }

      return <DashboardLayout />;
}

function DashboardLayout() {
      const trpc = useTRPC();
      const { pathname } = useLocation();
      const navigate = useNavigate();
      const hasSession = true;
      const isUnbrandedRoute = UNBRANDED_ROUTES.has(pathname);
      const currentBrandSlug = isUnbrandedRoute ? null : pathname.split("/")[1] || null;
      const searchSpecificationIdFromPath = getSearchSpecificationIdFromPath(pathname);

      const {
            data: brands,
            isLoading: isLoadingBrands,
            isFetching: isFetchingBrands,
      } = useQuery(
            trpc.brands.getOrganizationBrands.queryOptions(
                  {
                        skip: 0,
                        take: 100,
                  },
                  {
                        enabled: hasSession,
                  },
            ),
      );
      const {
            data: brandFromSlug,
            isLoading: isLoadingBrandFromSlug,
            isFetching: isFetchingBrandFromSlug,
      } = useQuery(
            trpc.brands.getBrandBySlug.queryOptions(
                  { slug: currentBrandSlug ?? "" },
                  { enabled: hasSession && !!currentBrandSlug },
            ),
      );
      const { data: breadcrumbSearchSpecification } = useQuery(
            trpc.searchSpecifications.getSearchSpecification.queryOptions(
                  { id: searchSpecificationIdFromPath ?? "" },
                  { enabled: hasSession && !!searchSpecificationIdFromPath },
            ),
      );

      const dashboardBrandRoutingState = useMemo(
            () => ({
                  hasSession,
                  isLoadingBrands,
                  isFetchingBrands,
                  brands,
                  isUnbrandedRoute,
                  currentBrandSlug,
                  hasBrandFromSlug: Boolean(brandFromSlug),
                  isLoadingBrandFromSlug,
                  isFetchingBrandFromSlug,
            }),
            [
                  brandFromSlug,
                  brands,
                  currentBrandSlug,
                  isFetchingBrandFromSlug,
                  isFetchingBrands,
                  isLoadingBrandFromSlug,
                  isLoadingBrands,
                  isUnbrandedRoute,
                  hasSession,
            ],
      );

      const selectedBrandId = useBrandStore((state) => state.selectedBrandId);
      const setSelectedBrandId = useBrandStore((state) => state.setSelectedBrandId);
      const selectedBrand = brands?.find((brand) => brand.id === selectedBrandId) ?? brandFromSlug ?? undefined;

      const currentSearchSpecificationId = useSearchSpecificationStore((state) => state.currentSearchSpecificationId);
      const setCurrentSearchSpecificationId = useSearchSpecificationStore(
            (state) => state.setCurrentSearchSpecificationId,
      );

      // Query to validate search specification if one exists in localStorage
      const { data: searchSpecs } = useQuery({
            ...trpc.searchSpecifications.getBrandSearchSpecifications.queryOptions({
                  brandId: selectedBrandId ?? "",
            }),
            enabled: !!selectedBrandId && !!currentSearchSpecificationId,
      });

      // Validate selectedBrandId from localStorage against available brands
      useEffect(() => {
            if (shouldDeferDashboardBrandResolution(dashboardBrandRoutingState)) return;

            // Keep selectedBrandId in sync with the brand slug in the URL.
            if (brandFromSlug) {
                  if (selectedBrandId !== brandFromSlug.id) {
                        setSelectedBrandId(brandFromSlug.id);
                  }
                  return;
            }

            if (!brands) {
                  return;
            }

            // If we have a selectedBrandId but it doesn't exist in current user's brands, clear it
            if (selectedBrandId && !brands.find((brand) => brand.id === selectedBrandId)) {
                  console.warn(
                        `[localStorage cleanup] selectedBrandId "${selectedBrandId}" not found in user's brands. Clearing.`,
                  );
                  setSelectedBrandId(null);
                  return;
            }

            // If no selectedBrandId but we have brands, select the first one
            if (!selectedBrandId && brands.length > 0) {
                  setSelectedBrandId(brands[0].id);
            }
      }, [
            brandFromSlug,
            brands,
            dashboardBrandRoutingState,
            selectedBrandId,
            setSelectedBrandId,
      ]);

      // Validate currentSearchSpecificationId from localStorage
      useEffect(() => {
            if (!currentSearchSpecificationId || !searchSpecs) return;

            // Check if the search specification exists in the current brand's specifications
            const specExists = searchSpecs.items.some((spec) => spec.id === currentSearchSpecificationId);

            if (!specExists) {
                  console.warn(
                        `[localStorage cleanup] currentSearchSpecificationId "${currentSearchSpecificationId}" not found in brand's search specifications. Clearing.`,
                  );
                  setCurrentSearchSpecificationId(null);
            }
      }, [currentSearchSpecificationId, searchSpecs, setCurrentSearchSpecificationId]);

      useEffect(() => {
            if (
                  shouldRedirectDashboardToBrandOnboarding({
                        ...dashboardBrandRoutingState,
                  })
            ) {
                  navigate({ to: "/onboarding/brand/new", replace: true });
            }
      }, [dashboardBrandRoutingState, navigate]);

      const displayPricingModal = usePricingPageStore((state) => state.displayPricingModal);
      const breadcrumbSearchSpecificationName = breadcrumbSearchSpecification?.name?.trim() || null;

      let rootBreadCrumb: React.ReactNode;
      if (isUnbrandedRoute) {
            const rootBreadcrumbLabel =
                  pathname === "/api-access"
                        ? "API Access"
                        : toTitleCase(pathname.split("/").slice(1).join("/").replace(/-/g, " "));
            rootBreadCrumb = (
                  <Link to={pathname}>{rootBreadcrumbLabel}</Link>
            );
      } else {
            rootBreadCrumb =
                  isLoadingBrands || !selectedBrand ? (
                        <Skeleton className="h-4 w-24" />
                  ) : (
                        <Link to={`/$brand/dashboard`} params={{ brand: selectedBrand.slug }}>
                              {toTitleCase(selectedBrand.name)}
                        </Link>
                  );
      }
      return (
            <>
                  <SidebarProvider
                        className="min-h-svh overflow-hidden"
                        style={
                              {
                                    "--sidebar-width": "19rem",
                                    "--sidebar-width-mobile": "19rem",
                              } as React.CSSProperties
                        }
                  >
                        <AppSidebar />
                        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                              <SidebarInset className="flex min-h-0 min-w-0 flex-col overflow-hidden pt-0">
                                    <header className="sticky top-0 z-20 flex min-h-16 shrink-0 flex-wrap items-center gap-3 border-b bg-background px-3 py-2 sm:px-4">
                                          <div className="flex min-w-0 flex-1 items-center gap-2">
                                                <SidebarTrigger className="-ml-1" />
                                                <Separator
                                                      orientation="vertical"
                                                      className="mr-2 data-[orientation=vertical]:h-4"
                                                />
                                                <Breadcrumb className="min-w-0 font-bold">
                                                      <BreadcrumbList className="min-w-0 flex-nowrap text-muted-foreground">
                                                            <BreadcrumbItem className="hidden md:block">
                                                                  {rootBreadCrumb}
                                                            </BreadcrumbItem>
                                                            {pathname.split("/").length > 2 && (
                                                                  <>
                                                                        <BreadcrumbSeparator className="hidden md:block" />
                                                                        {(() => {
                                                                              const pathSegments = pathname
                                                                                    .split("/")
                                                                                    .slice(2);
                                                                              const breadcrumbItems = [];

                                                                              for (let i = 0; i < pathSegments.length; i++) {
                                                                                    const segment = pathSegments[i];
                                                                                    const isLast =
                                                                                          i === pathSegments.length - 1;

                                                                                    // Check if segment looks like a UUID (contains multiple dashes and is long)
                                                                                    const isUUID =
                                                                                          segment.includes("-") &&
                                                                                          segment.length > 20;
                                                                                    const displayText =
                                                                                          getDashboardBreadcrumbDisplayText(
                                                                                                segment,
                                                                                                {
                                                                                                      fallbackText: isUUID
                                                                                                            ? segment
                                                                                                            : toTitleCase(
                                                                                                                    segment.replace(
                                                                                                                          /-/g,
                                                                                                                          " ",
                                                                                                                    ),
                                                                                                              ),
                                                                                                      searchSpecificationId:
                                                                                                            searchSpecificationIdFromPath,
                                                                                                      searchSpecificationName:
                                                                                                            breadcrumbSearchSpecificationName,
                                                                                                },
                                                                                          );

                                                                                    if (isLast) {
                                                                                          // Last segment is always a page, not a link
                                                                                          breadcrumbItems.push(
                                                                                                <BreadcrumbItem key={i}>
                                                                                                      <BreadcrumbPage>
                                                                                                            {displayText}{" "}
                                                                                                      </BreadcrumbPage>
                                                                                                </BreadcrumbItem>,
                                                                                          );
                                                                                    } else {
                                                                                          // Intermediate segments are links
                                                                                          const linkPath =
                                                                                                `/$brand/` +
                                                                                                pathSegments
                                                                                                      .slice(0, i + 1)
                                                                                                      .join("/");
                                                                                          breadcrumbItems.push(
                                                                                                <BreadcrumbItem key={i}>
                                                                                                      <Link
                                                                                                            to={linkPath}
                                                                                                            params={{
                                                                                                                  brand:
                                                                                                                        selectedBrand?.slug ||
                                                                                                                        "",
                                                                                                            }}
                                                                                                      >
                                                                                                            {displayText}
                                                                                                      </Link>
                                                                                                </BreadcrumbItem>,
                                                                                          );

                                                                                          // Add separator after each non-last item
                                                                                          if (i < pathSegments.length - 1) {
                                                                                                breadcrumbItems.push(
                                                                                                      <BreadcrumbSeparator
                                                                                                            key={`sep-${i}`}
                                                                                                            className="hidden md:block"
                                                                                                      />,
                                                                                                );
                                                                                          }
                                                                                    }
                                                                              }

                                                                              return breadcrumbItems;
                                                                        })()}
                                                                  </>
                                                            )}
                                                      </BreadcrumbList>
                                                </Breadcrumb>
                                          </div>
                                          <UpgradeBanner className="w-full sm:ml-auto sm:w-auto" />
                                    </header>
                                    <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden ">
                                          <div className="min-w-0">
                                                <Outlet />
                                          </div>
                                    </div>
                              </SidebarInset>
                        </div>
                  </SidebarProvider>
                  {displayPricingModal && <ProductDisplay />}
            </>
      );
}
